
/**
 * Functions for generating activity content
 */

function getRandomDetail(type: string, babyName: string): string {
  const details: Record<string, string[]> = {
    tummyTime: [
      `${babyName} managed to lift their head higher than ever before, showcasing their growing strength.`,
      `I could see the determination in ${babyName}'s eyes as they pushed up on their little arms.`,
      `The focus on ${babyName}'s face during this exercise was absolutely precious.`,
      `${babyName} surprised me by staying in position longer than usual, gaining confidence each day.`,
      `I noticed ${babyName}'s neck and shoulder muscles getting visibly stronger during today's session.`,
      `${babyName} seemed to enjoy the perspective from this position, taking in the world from a new angle.`,
      `The way ${babyName} held their head steady shows how much progress they're making day by day.`
    ],
    bathTime: [
      `${babyName} splashed with such enthusiasm that we both ended up giggling.`,
      `The warm water seemed to soothe ${babyName}, who cooed contentedly throughout.`,
      `${babyName} was fascinated by the bubbles, reaching out to try and catch them with curious fingers.`,
      `Bath time has become a special ritual that ${babyName} clearly looks forward to each day.`,
      `${babyName}'s delighted kicks sent water everywhere, but the joy on their face was worth every splash.`,
      `I noticed how ${babyName} relaxed as soon as they felt the warm water around them.`,
      `${babyName} watched, mesmerized, as water dripped from their fingers during our bath routine.`
    ],
    reading: [
      `${babyName}'s eyes widened at the colorful illustrations, tiny hands reaching out to touch the pages.`,
      `I swear ${babyName} was trying to mimic the sounds as I read aloud, making the sweetest attempts at communication.`,
      `There's something magical about holding ${babyName} close while sharing stories that I hope to remember forever.`,
      `${babyName} seemed particularly drawn to the rhythm of the words, almost swaying along to the cadence.`,
      `I noticed ${babyName} starting to recognize certain characters in their favorite book.`,
      `${babyName} listened so attentively, their expression changing with the emotions of the story.`,
      `Each time we reread ${babyName}'s favorite book, they seem to discover something new on the pages.`
    ]
  };
  
  const options = details[type] || [
    `${babyName} showed such progress and joy in this activity.`,
    `It's amazing to witness ${babyName}'s development through these simple moments.`,
    `These precious times with ${babyName} are building beautiful memories.`,
    `I'm amazed at how quickly ${babyName} is learning and growing through everyday experiences.`,
    `Watching ${babyName} engage with the world is a constant source of wonder.`,
    `These little moments with ${babyName} are the ones I know I'll treasure most.`,
    `${babyName}'s enthusiasm for discovery makes every activity feel special and meaningful.`
  ];
  
  return options[Math.floor(Math.random() * options.length)];
}

function getActivitySentence(activity: string, babyName: string): string {
  const sentences: Record<string, string[]> = {
    'Feeding': [
      `${babyName} ate so well today, pausing occasionally to flash a milky smile.`,
      `Feeding time was peaceful today, with ${babyName} seeming content and satisfied.`,
      `During feeds today, ${babyName} and I shared such special bonding moments.`,
      `${babyName} nursed with perfect focus today, their little hand resting gently against me.`,
      `I noticed ${babyName} developing more of a routine with feeding times today.`,
      `The quiet moments during feeding gave ${babyName} and me a chance to really connect today.`,
      `${babyName} seemed particularly hungry today, finishing feeds with satisfied contentment.`
    ],
    'Outdoors': [
      `Our time outside today was magical. ${babyName} seemed mesmerized by the rustling leaves and gentle breeze.`,
      `${babyName} was wide-eyed during our outdoor adventure, taking in all the colors and sounds of nature.`,
      `The fresh air seemed to delight ${babyName} during our walk today.`,
      `${babyName} tracked the movement of clouds with curious eyes during our outdoor time.`,
      `I pointed out birds to ${babyName} today, and they followed the movement with growing awareness.`,
      `The dappled sunlight through trees created patterns that completely captivated ${babyName}.`,
      `${babyName} seemed to breathe more deeply outdoors, taking in all the new scents and sensations.`
    ],
    'Music': [
      `${babyName} responded so beautifully to music today, swaying gently to the rhythm.`,
      `We danced around the living room, and ${babyName}'s face lit up with each new song.`,
      `The lullabies we listened to today seemed to cast a spell of joy over ${babyName}.`,
      `${babyName} startled me by bouncing in perfect time to the beat of our favorite song.`,
      `I noticed ${babyName} has clear preferences forming - responding more enthusiastically to certain melodies.`,
      `${babyName}'s whole body seemed to light up when their favorite tune started playing.`,
      `We discovered a new song that instantly calmed ${babyName} today, adding it to our special playlist.`
    ],
    'Nap': [
      `${babyName} slept so peacefully today, little eyelids fluttering with dreams.`,
      `Nap time went smoothly, with ${babyName} falling asleep while I gently stroked their cheek.`,
      `During today's nap, ${babyName} looked like a tiny angel, completely at peace.`,
      `${babyName} fought sleep briefly before surrendering to heavy eyelids and deep, restful breathing.`,
      `I watched ${babyName} cycling through sleep states during their nap, tiny expressions passing over their face.`,
      `${babyName}'s nap routine is becoming more predictable, making our days flow more smoothly.`,
      `The house holds a special kind of quiet when ${babyName} is peacefully napping.`
    ],
    'Play': [
      `${babyName} showed incredible focus during playtime today, examining each toy with careful attention.`,
      `Playtime was filled with joyful discoveries as ${babyName} explored different textures and sounds.`,
      `${babyName} seems to develop new skills daily - today they deliberately reached for their favorite toy.`,
      `I noticed ${babyName} showing preference for certain toys, returning to them with obvious intention.`,
      `The concentration on ${babyName}'s face during play shows their developing cognitive abilities.`,
      `${babyName} practiced cause and effect today, delighted when their actions produced interesting results.`,
      `Our play session turned into a fit of giggles when ${babyName} discovered a new squeaky toy.`
    ]
  };
  
  const defaultResponses = [
    `We spent some wonderful time ${activity.toLowerCase()} today. ${babyName} seemed to really enjoy it.`,
    `${activity} was a highlight of our day, with ${babyName} fully engaged and present.`,
    `During ${activity.toLowerCase()}, I noticed how much ${babyName} has grown and changed.`,
    `${babyName} approached ${activity.toLowerCase()} with their characteristic curiosity and enthusiasm.`,
    `I cherished our time ${activity.toLowerCase()} together today, watching ${babyName} flourish.`,
    `${activity} offered ${babyName} new opportunities for learning and discovery today.`,
    `There was a special moment during ${activity.toLowerCase()} when ${babyName} seemed to make a new connection.`
  ];
  
  const responses = sentences[activity] || defaultResponses;
  return responses[Math.floor(Math.random() * responses.length)];
}

export function generateActivitiesContent(babyName: string, activities: string[]): string {
  if (activities.length === 0) return '';
  
  const paragraphs = [];
  
  if (activities.includes('Tummy time') && activities.includes('Play')) {
    paragraphs.push(`During tummy time today, ${babyName} showed such determination. ${getRandomDetail('tummyTime', babyName)}`);
    activities = activities.filter(a => a !== 'Tummy time' && a !== 'Play');
  }
  
  if (activities.includes('Bath time')) {
    paragraphs.push(`Bath time was a delight! ${getRandomDetail('bathTime', babyName)}`);
    activities = activities.filter(a => a !== 'Bath time');
  }
  
  if (activities.includes('Reading')) {
    paragraphs.push(`We enjoyed some quiet reading time together. ${getRandomDetail('reading', babyName)}`);
    activities = activities.filter(a => a !== 'Reading');
  }
  
  activities.forEach(activity => {
    paragraphs.push(`${getActivitySentence(activity, babyName)}`);
  });
  
  return paragraphs.join('\n\n');
}
